import { For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";

// Structure réelle renvoyée par GET /api/super-admin/db-metrics :
//   { ok, database: { name, sizeBytes, connections:{active,idle,max},
//                     tables:[{name,sizeBytes,rowCount}], cacheHitRatio,
//                     slowQueries:[{query,meanTime,calls}] } }
interface DBMetrics {
  ok: boolean;
  database: {
    name: string;
    sizeBytes: number;
    connections: { active: number; idle: number; max: number };
    tables: { name: string; sizeBytes: number; rowCount: number }[];
    cacheHitRatio: number;
    slowQueries?: { query: string; calls: number; meanTime: number }[];
  };
}

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return r.json() as Promise<T>;
}

export function SuperAdminDbMetricsPage() {
  const q = createQuery<DBMetrics>(() => ({
    queryKey: ["super-admin-db-metrics"],
    queryFn: () => jget<DBMetrics>("/api/super-admin/db-metrics"),
    refetchInterval: 10_000,
  }));

  return (
    <div class="plugins-console" style={{ overflow: "auto" }}>
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          Métriques Base de données
          <span class="plugins-console-sub">PostgreSQL — health + taille + requêtes lentes</span>
        </span>
      </div>

      <Show when={q.isLoading}>
        <div style={{ padding: "20px", color: "var(--text-4)" }}>Chargement…</div>
      </Show>

      <Show when={q.error}>
        <div class="bug-report-message" data-kind="err" style={{ "margin-bottom": "10px" }}>
          {q.error instanceof Error ? q.error.message : String(q.error)}
        </div>
      </Show>

      <Show when={!q.isLoading && q.data?.database}>
        {(_) => {
          const db = () => q.data!.database;
          return (
            <>
              <div
                style={{
                  display: "grid",
                  "grid-template-columns": "repeat(auto-fit,minmax(140px,1fr))",
                  gap: "10px",
                  "margin-bottom": "14px",
                }}
              >
                <div class="card" style={{ padding: "12px", "text-align": "center" }}>
                  <div style={{ "font-size": "20px", "font-weight": "700", color: "var(--ok, #4ade80)" }}>
                    {db().connections.active}
                  </div>
                  <div style={{ "font-size": "11px", color: "var(--text-4)" }}>Connexions actives</div>
                </div>
                <div class="card" style={{ padding: "12px", "text-align": "center" }}>
                  <div style={{ "font-size": "20px", "font-weight": "700", color: "var(--info, #60a5fa)" }}>
                    {db().connections.idle}
                  </div>
                  <div style={{ "font-size": "11px", color: "var(--text-4)" }}>Connexions idle</div>
                </div>
                <div class="card" style={{ padding: "12px", "text-align": "center" }}>
                  <div style={{ "font-size": "20px", "font-weight": "700", color: "var(--warn, #fbbf24)" }}>
                    {db().connections.max}
                  </div>
                  <div style={{ "font-size": "11px", color: "var(--text-4)" }}>Max connexions</div>
                </div>
                <div class="card" style={{ padding: "12px", "text-align": "center" }}>
                  <div style={{ "font-size": "20px", "font-weight": "700", color: "var(--text-1)" }}>
                    {(db().sizeBytes / (1024 * 1024)).toFixed(1)} MB
                  </div>
                  <div style={{ "font-size": "11px", color: "var(--text-4)" }}>Taille DB</div>
                </div>
                <div class="card" style={{ padding: "12px", "text-align": "center" }}>
                  <div style={{ "font-size": "20px", "font-weight": "700", color: "var(--ok, #4ade80)" }}>
                    {(db().cacheHitRatio * 100).toFixed(1)}%
                  </div>
                  <div style={{ "font-size": "11px", color: "var(--text-4)" }}>Cache hit ratio</div>
                </div>
              </div>

              <div class="rmm-table-wrap" style={{ "margin-bottom": "14px" }}>
                <div style={{ padding: "8px 10px", "font-weight": "600", "font-size": "13px" }}>Tables</div>
                <table class="rmm-table">
                  <thead>
                    <tr>
                      <th>Table</th>
                      <th>Lignes (est.)</th>
                      <th>Taille</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={db().tables || []}>
                      {(t) => (
                        <tr>
                          <td style={{ "font-weight": "500" }}>{t.name}</td>
                          <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>
                            {t.rowCount != null ? t.rowCount.toLocaleString() : "—"}
                          </td>
                          <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>
                            {t.sizeBytes > 1024 * 1024
                              ? `${(t.sizeBytes / (1024 * 1024)).toFixed(1)} MB`
                              : t.sizeBytes > 1024
                                ? `${(t.sizeBytes / 1024).toFixed(1)} KB`
                                : `${t.sizeBytes} B`}
                          </td>
                        </tr>
                      )}
                    </For>
                    <Show when={(db().tables || []).length === 0}>
                      <tr>
                        <td colSpan={3} style={{ color: "var(--text-4)", "text-align": "center" }}>
                          Aucune donnée.
                        </td>
                      </tr>
                    </Show>
                  </tbody>
                </table>
              </div>

              <Show when={(db().slowQueries || []).length > 0}>
                <div class="rmm-table-wrap">
                  <div style={{ padding: "8px 10px", "font-weight": "600", "font-size": "13px" }}>
                    Requêtes lentes (pg_stat_statements)
                  </div>
                  <table class="rmm-table">
                    <thead>
                      <tr>
                        <th>Appels</th>
                        <th>Temps moyen (ms)</th>
                        <th>Requête</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={db().slowQueries}>
                        {(sq) => (
                          <tr>
                            <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>{sq.calls}</td>
                            <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>
                              {sq.meanTime != null ? sq.meanTime.toFixed(2) : "—"}
                            </td>
                            <td
                              style={{
                                "font-size": "11px",
                                "font-family": "var(--font-mono)",
                                "max-width": "400px",
                                overflow: "hidden",
                                "text-overflow": "ellipsis",
                                "white-space": "nowrap",
                              }}
                              title={sq.query}
                            >
                              {sq.query}
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </Show>
            </>
          );
        }}
      </Show>
    </div>
  );
}
